import React, { useContext, useEffect, useMemo, useState } from "react"
import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    TextField,
    Typography,
} from "@mui/material"
import { useMutation, useQueryClient } from "react-query"
import { useParams } from "react-router-dom"
import { useSnackbar } from "notistack"

import AuthContext from "../Context/auth_provider.jsx"
import useBreakPoints from "../Hooks/useBreakPoints.js"
import useFetchEntities from "../Hooks/useFetchEntity.js"
import useAxiosPrivate from "../Hooks/useAxiosPrivate.js"

function UpdatePreRIDEPrograms({ user, open, setOpen, invited, refetch }) {
    const { user_id: id, prerides, phone } = user || {}

    const { tId } = useParams()
    const { xs, md } = useBreakPoints()

    const queryClient = useQueryClient()

    const [progs, setProgs] = useState([])

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        if (prerides) {
            const assignedProgs = prerides?.map((el) => ({
                name: el.name,
                id: el.id,
            }))
            setProgs(assignedProgs)
        }
    }, [prerides, open])
    const teamEndPoint = tId ? `teams/${tId}/prerides` : "prerides"
    const { data: companyPrograms } = useFetchEntities(teamEndPoint)

    // STORE MAPPING OF PROGAM NAME AND ID OF THE COMPANY PROGRAMS
    const progNameId = useMemo(
        () =>
            companyPrograms?.data?.data?.map((prgms) => ({
                name: prgms.name,
                id: prgms.id,
            })),
        [companyPrograms]
    )

    const axiosPrivate = useAxiosPrivate()
    const { companyId, teamId } = useContext(AuthContext)
    const domain = teamId ? `team/${teamId}` : `corporate/${companyId}`

    const endPoint = tId ? `${domain}/teams/${tId}` : domain

    const { mutate, isLoading } = useMutation(
        (data) =>
            axiosPrivate
                .patch(`${endPoint}/users/${id}/prerides`, data)
                .then((res) => res.data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [domain, "users"],
                })
                refetch()
                enqueueSnackbar("Updated successfully", {
                    variant: "success",
                })
                setOpen(false)
            },
            onError: (error) => {
                const fieldError =
                    error.response?.data?.error?.field_error?.[0]?.description
                const errorMessage = error.response?.data?.error?.message
                enqueueSnackbar(
                    fieldError || errorMessage || "Request Failed",
                    { variant: "error" }
                )
            },
        }
    )

    const { mutate: updateInvited, isLoading: loadingInvited } = useMutation(
        (data) =>
            axiosPrivate
                .patch(`${domain}/users/invite/${phone}`, data)
                .then((res) => res?.data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [domain, "users/invite"],
                })
                refetch()
                enqueueSnackbar("Updated successfully", {
                    variant: "success",
                })
                setOpen(false)
            },
            onError: (error) => {
                const fieldError =
                    error.response?.data?.error?.field_error?.[0]?.description
                const errorMessage = error.response?.data?.error?.message
                enqueueSnackbar(
                    fieldError || errorMessage || "Request Failed",
                    { variant: "error" }
                )
            },
        }
    )

    const handleChange = (e, val) => setProgs(val)

    const handleUpdate = () => {
        if (invited) {
            updateInvited({
                preride_program_id: progs.map((prog) => prog.id),
            })
        } else {
            mutate({
                user_id: id,
                preride_program_id: progs.map((prog) => prog.id),
            })
        }
    }

    return (
        <Dialog
            fullScreen={xs && !md}
            maxWidth="md"
            PaperProps={{
                sx: {
                    minWidth: md && "70ch",
                },
            }}
            onClose={() => setOpen(false)}
            open={open}
        >
            <DialogTitle
                sx={{
                    margin: "auto",
                    mb: "1em",
                    textAlign: "center",
                    backgroundColor: "common.main",
                    width: "100%",
                }}
            >
                Edit pre-RIDE Spending
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1em",
                    p: "1em",
                }}
            >
                <Autocomplete
                    multiple
                    value={progs}
                    getOptionLabel={(option) => option?.name}
                    onChange={handleChange}
                    options={progNameId || []}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    renderOption={(props, option) => (
                        <li
                            {...props}
                            style={{
                                pointerEvents: progs.includes(option)
                                    ? "none"
                                    : "auto",
                                opacity: progs.includes(option) ? 0.5 : 1,
                            }}
                        >
                            <Typography variant="subtitle1">
                                {option.name}
                            </Typography>
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} />}
                    sx={{
                        minWidth: md && "40ch",
                    }}
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: "1em" }}>
                <Button
                    onClick={() => setOpen(false)}
                    variant="contained"
                    color="common"
                >
                    Cancel
                </Button>
                <Button
                    disabled={progs?.length === 0}
                    startIcon={
                        isLoading || loadingInvited ? (
                            <CircularProgress
                                size="1em"
                                sx={{ color: "white" }}
                            />
                        ) : null
                    }
                    variant="contained"
                    onClick={handleUpdate}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default UpdatePreRIDEPrograms
